import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {number | undefined} */
  #adoptHoverFrame;

  /** @type {ReturnType<typeof setTimeout>[]} */
  #adoptRetryTimers = [];

  /** @type {number | undefined} */
  #healFrame;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointermove', this.#onIdlePointerMove);
    window.addEventListener('pageswap', this.#onPageSwap);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#adoptHoveredItem();

    /* One replay at upgrade is not enough. On a navigation with the pointer
     * at rest over the nav - click a menu link, land on the next page with
     * the cursor still on the item - the browser recomputes `:hover` on its
     * own schedule, usually after the first frame or once the view
     * transition settles, which can be well after this element upgrades.
     * Chromium then also dispatches the matching pointerenter and the menu
     * opens; Safari updates `:hover` without dispatching the boundary event,
     * so nothing reaches activate() and the item sits there bold with the
     * panel shut. Re-run the adoption on a short cadence so a hover that the
     * style system already sees is picked up as soon as it exists, however
     * late the browser computed it. Each pass is a no-op when a menu is open
     * or nothing is hovered. */
    for (const delay of [150, 400, 900, 1800, 3200]) {
      this.#adoptRetryTimers.push(setTimeout(() => this.#adoptHoveredItem(), delay));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointermove', this.#onIdlePointerMove);
    window.removeEventListener('pageswap', this.#onPageSwap);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    for (const timer of this.#adoptRetryTimers) clearTimeout(timer);
    this.#adoptRetryTimers = [];
    if (this.#adoptHoverFrame !== undefined) {
      cancelAnimationFrame(this.#adoptHoverFrame);
      this.#adoptHoverFrame = undefined;
    }
    if (this.#healFrame !== undefined) {
      cancelAnimationFrame(this.#healFrame);
      this.#healFrame = undefined;
    }
  }

  /**
   * Open the submenu for an item the pointer is already resting on.
   *
   * The nav is painted as soon as the HTML arrives, but this file is a low
   * priority ES module, so it runs a good deal later - about 150ms behind first
   * paint on an unthrottled desktop load, 425ms on fast 4G, 2.7s on slow 4G. A
   * `pointerenter` that lands in that gap is delegated by component.js, which
   * walks up from the list item looking for a `Component` to call `activate`
   * on. `<header-menu>` has not been upgraded yet, and its tag name does not
   * end in `-component`, so the walk goes straight past it to
   * `<header-component>` - which has no `activate` - and the hover is dropped.
   *
   * By then the pointer is already inside the item, so there is no boundary
   * left to cross and the browser never fires another `pointerenter`. The link
   * sits there looking hovered, because that part is pure CSS, with no panel
   * under it, and stays that way until the pointer leaves the item and comes
   * back. That is the intermittent "entering the menu link does not open the
   * submenu" report: it is a load race, which is why it never reproduced under
   * a mouse driven around an already settled page.
   *
   * The browser keeps `:hover` current without anyone listening, so it still
   * knows where the pointer is. Ask it, and replay the enter that was dropped.
   * A frame late so `super.connectedCallback()` has resolved the refs and the
   * header has been measured.
   */
  #adoptHoveredItem() {
    if (this.#adoptHoverFrame !== undefined) cancelAnimationFrame(this.#adoptHoverFrame);
    this.#adoptHoverFrame = requestAnimationFrame(() => {
      this.#adoptHoverFrame = undefined;

      if (this.#state.activeItem) return;

      const hovered = this.querySelector('.menu-list__list-item:hover');
      if (!(hovered instanceof HTMLElement)) return;

      hovered.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    });
  }

  /**
   * Self-heal for a dropped hover, driven by the pointer itself.
   *
   * The state machine opens the menu from `pointerenter`, but the boundary
   * event is not guaranteed: Safari updates the hover chain without
   * dispatching enter/leave when content changes under a stationary pointer,
   * and a navigation can land with the cursor already inside an item so no
   * boundary is ever crossed again. In that state the item shows its `:hover`
   * styling - the browser knows the pointer is there - while activate() was
   * never called, and because the pointer never exits the item there is no
   * event left that could recover it. That is the "hover works but the menu
   * does not open" report.
   *
   * `pointermove` is dispatched on real movement in every engine regardless
   * of those quirks, so while no item is active, any movement over the nav
   * re-checks the style system's `:hover` and replays the enter that never
   * arrived. One rAF-throttled query per frame at most, only during movement
   * over the nav, early-out while a menu is open.
   */
  #onIdlePointerMove = () => {
    if (this.#state.activeItem || this.#healFrame !== undefined) return;
    this.#healFrame = requestAnimationFrame(() => {
      this.#healFrame = undefined;
      if (this.#state.activeItem) return;

      const hovered = this.querySelector('.menu-list__list-item:hover');
      if (!(hovered instanceof HTMLElement)) return;

      /* Only replay when the hover is not already reflected in the menu state:
       * activate() early-outs on the active item anyway, so this is belt and
       * braces against dispatch loops. */
      hovered.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    });
  };

  /**
   * Close the menu before the browser captures this page for a
   * cross-document view transition.
   *
   * `@view-transition { navigation: auto }` is enabled, and the old page is
   * snapshotted right after `pageswap` fires. Navigating via the menu -
   * clicking a top-level item or a mega-menu link - therefore froze the OPEN
   * panel into the outgoing snapshot, which then dissolved over the next
   * page: seen as the submenu flashing up and vanishing on arrival. Closing
   * here keeps the panel out of the frame. The `data-page-swapping` attribute
   * lets CSS cut through the `:hover` rule that keeps a hovered submenu
   * visible (the pointer is usually inside the panel when a mega-menu link is
   * clicked) and through the 360ms height/clip transitions that would
   * otherwise still be mid-flight when the snapshot is taken.
   */
  #onPageSwap = () => {
    this.headerComponent?.setAttribute('data-page-swapping', '');
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Restore after a back/forward-cache resurrection. The pageswap teardown
   * above ran when the page was frozen, so lift the paint guard and adopt
   * whatever the pointer is resting on now.
   */
  #onPageShow = () => {
    this.headerComponent?.removeAttribute('data-page-swapping');
    this.#adoptHoveredItem();
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      /* This frame can outlive the activation that scheduled it - a real
       * pointerleave may have deactivated the menu in between. The stored
       * coordinates are then stale, and dispatching an enter from them would
       * reopen a menu the pointer has already left. Only reconcile while a
       * menu is actually active. */
      if (!this.#state.activeItem) return;
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    const boundaryBottom = boundary?.getBoundingClientRect().bottom ?? 0;

    /* The box only has to bridge the gap the pointer crosses on its way down
     * from the item into the submenu below - the header's bottom padding. It
     * used to start at the item's top edge, which meant that while the pointer
     * was moving it covered every other item in the row and the pointer could
     * not reach them. Start it at the item's bottom edge instead so the rest
     * of the nav keeps its own hit testing.
     *
     * The box is positioned against the link's offset parent, so both offsets
     * are measured relative to that element's padding box. */
    const container = /** @type {HTMLElement | null} */ (item.offsetParent) ?? this.headerComponent;
    const containerTop = container ? container.getBoundingClientRect().top + container.clientTop : 0;

    item.style.setProperty('--box-top', `${rect.bottom - containerTop}px`);
    item.style.setProperty('--box-height', `${Math.max(0, boundaryBottom - rect.bottom)}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-top');
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      /* Then force a style+layout pass while the item is still collapsed.
       *
       * A closed submenu carries `content-visibility: auto`, so its entire
       * subtree is skipped and has never had a computed style. Marking the
       * item expanded first means the subtree's first computed style is
       * already the open one - and a transition with no start value does not
       * run at all, so the panel's links snapped to full opacity instead of
       * fading in behind the reveal. Reading a layout property here un-skips
       * the subtree and pins its contents at opacity 0, giving the fade
       * something to animate from on the very first open. */
      void submenu.offsetHeight;
    }

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              const height = submenu.offsetHeight;
              this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
              /* Keep both height variables in step. The panel's position and
               * clip are derived from the PAIR of --submenu-height and
               * --full-open-header-height; updating only the first leaves
               * the clip rect sized for the stale height - in the worst case
               * (activation measured 0 because content had not landed yet)
               * an empty clip, i.e. a menu that is open by every attribute
               * yet paints nothing. */
              this.#setFullOpenHeaderHeight(height, this.#getHeaderVisibleHeight());
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    /* The leave can belong to an item that is no longer the active one. While
     * the safety box is displayed it owns the hit test for the strip it covers,
     * so the browser reports the boundary crossing only once the box comes
     * down - by which point the pointer is already over another item and that
     * item has been activated. Acting on that stale leave tore down the menu
     * that had just opened, which reads as the submenu flashing open and
     * immediately disappearing. Only the active item's own leave may close it. */
    const leavingItem = findMenuItem(event.target);
    if (leavingItem && this.#state.activeItem && leavingItem !== this.#state.activeItem) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Close even while the overflow menu is hovered
   *   (used on pageswap, where the page is being left regardless).
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
